import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Typography, TextField, Button, Card, CardContent, IconButton,
  MenuItem, Divider, ToggleButtonGroup, ToggleButton, Alert, Tooltip, CircularProgress,
} from '@mui/material';
import { Add, Delete, Download, Save, FlashOn, ReceiptLong, History } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/apiService';
import InvoicePreview from '../components/InvoicePreview';
import { colors } from '../theme/theme';
import { generateInvoicePdf } from '../utils/generateInvoicePdf';
import { computeInvoiceTotals, InvoiceLineItem, normalizeLineItem } from '../utils/invoiceCalculations';
import { resolveStudioLogoUrl } from '../utils/studioLogo';
import { DEFAULT_INVOICE_TERMS } from '../utils/invoiceDefaults';
import { isStudioScopedRole } from '../utils/roles';
import { useMyLocations } from '../hooks/useMyLocations';
import { studioIdOf } from '../utils/studioOf';
import BranchSelect from '../components/BranchSelect';

interface Party {
  name: string;
  address: string;
  gstin: string;
  email: string;
  phone: string;
  companyId?: string;
  country?: string;
  placeOfSupply?: string;
  logoUrl?: string;
}

const todayStr = () => new Date().toISOString().slice(0, 10);
const emptyItem = (): InvoiceLineItem => ({
  description: '', category: '', quantity: 1, rate: 0, cgstPercent: 2.5, sgstPercent: 2.5,
});
const money = (n: number, sym: string) =>
  `${sym}${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SS_BRANDING: Party = {
  name: 'SweatSocial', address: 'SweatSocial', gstin: '', email: 'hello@sweatsocial.club', phone: '', country: 'India', logoUrl: '',
};

const PAID_NOTE = 'Thanks for your business.';
const DUE_NOTE = 'Thanks for your business. Please make the payment by the due date.';
const noteForStatus = (s: string) => (s === 'paid' ? PAID_NOTE : DUE_NOTE);

const statePlaceOfSupply = (state?: string) => {
  if (!state) return '';
  const codes: Record<string, string> = {
    Karnataka: '29', Maharashtra: '27', Delhi: '07', 'Tamil Nadu': '33', Telangana: '36',
  };
  const code = codes[state];
  return code ? `${state} (${code})` : state;
};

const InvoiceGeneratorPage: React.FC = () => {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [editId, setEditId] = useState<string | null>(id || null);
  const isStudioRole = isStudioScopedRole(admin?.role);
  const isSuperAdmin = !isStudioRole;

  // Under "All locations" a studio role's new invoice has no implied issuer —
  // the switcher's __all__ is refused for writes — so it names the location.
  // An existing invoice is pinned to the location it was issued from.
  const { isAllLocations } = useMyLocations();
  const [invoiceBranch, setInvoiceBranch] = useState('');
  const [invoiceStudioId, setInvoiceStudioId] = useState<string | null>(null);
  const mustPickBranch = isStudioRole && isAllLocations && !editId;

  const previewRef = useRef<HTMLDivElement>(null);

  const [brandSource, setBrandSource] = useState<'studio' | 'sweatsocial'>(isStudioRole ? 'studio' : 'sweatsocial');
  const [studios, setStudios] = useState<any[]>([]);
  const [selectedStudioId, setSelectedStudioId] = useState<string>(isStudioRole ? (admin?.studio?._id || '') : '');

  const [invoiceNumber, setInvoiceNumber] = useState('INV-0001');
  const [invoiceDate, setInvoiceDate] = useState(todayStr());
  const [dueDate, setDueDate] = useState(todayStr());
  const [currencySymbol, setCurrencySymbol] = useState('₹');
  const [status, setStatus] = useState<'draft' | 'sent' | 'paid'>('paid');
  const [themeColor, setThemeColor] = useState<string>(colors.primary);
  const [logoBgColor, setLogoBgColor] = useState<string>('#ffffff');
  const [billFrom, setBillFrom] = useState<Party>({
    name: '', address: '', gstin: '', email: '', phone: '', companyId: '', country: 'India', placeOfSupply: '', logoUrl: '',
  });
  const [billTo, setBillTo] = useState<Party>({
    name: '', address: '', gstin: '', email: '', phone: '', placeOfSupply: '',
  });
  const [items, setItems] = useState<InvoiceLineItem[]>([emptyItem()]);
  const [discount, setDiscount] = useState(0);
  const [shipping, setShipping] = useState(0);
  const [notes, setNotes] = useState(PAID_NOTE);
  const [terms, setTerms] = useState(DEFAULT_INVOICE_TERMS);

  const [quick, setQuick] = useState({ name: '', amount: '', category: '' });
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [savedNumber, setSavedNumber] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const applyBranding = useCallback((studio: any | null) => {
    if (!studio) {
      setBillFrom(prev => ({ ...prev, ...SS_BRANDING }));
      setThemeColor(colors.primary);
      return;
    }
    const logoUrl = resolveStudioLogoUrl(studio);
    setBillFrom(prev => ({
      ...prev,
      name: studio.studioName || '',
      address: [studio.address, studio.city, studio.state, studio.pincode || studio.zipCode].filter(Boolean).join(', '),
      gstin: studio.gstNumber || '',
      email: studio.email || '',
      phone: studio.phoneNumber || '',
      companyId: studio.branchCode || studio.companyId || '',
      country: studio.country || 'India',
      placeOfSupply: statePlaceOfSupply(studio.state),
      logoUrl,
    }));
    if (studio.whitelabelConfig?.primaryColor) setThemeColor(studio.whitelabelConfig.primaryColor);
    else setThemeColor(colors.primary);
  }, []);

  useEffect(() => {
    if (!id) return;
    apiService.getInvoiceById(id).then((res: any) => {
      const inv = res.invoice;
      if (!inv) return;
      setEditId(inv._id);
      setInvoiceStudioId(studioIdOf(inv.studio));
      setInvoiceNumber(inv.invoiceNumber || '');
      setInvoiceDate((inv.invoiceDate || '').slice(0, 10) || todayStr());
      setDueDate(inv.dueDate ? String(inv.dueDate).slice(0, 10) : todayStr());
      setCurrencySymbol(inv.currencySymbol || '₹');
      setStatus(inv.status || 'paid');
      setThemeColor(inv.themeColor || colors.primary);
      setLogoBgColor(inv.logoBgColor || 'transparent');
      setBillFrom({
        name: '', address: '', gstin: '', email: '', phone: '', companyId: '', country: 'India',
        placeOfSupply: '', logoUrl: '', ...(inv.billFrom || {}),
      });
      setBillTo({
        name: '', address: '', gstin: '', email: '', phone: '', placeOfSupply: '', ...(inv.billTo || {}),
      });
      const its = (inv.lineItems || []).map((it: any) => normalizeLineItem(it));
      setItems(its.length ? its : [emptyItem()]);
      setDiscount(inv.discount || 0);
      setShipping(inv.shipping || 0);
      setNotes(inv.notes || '');
      setTerms(inv.terms || DEFAULT_INVOICE_TERMS);
    }).catch(() => setError('Failed to load invoice'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Studio role: fetch full studio record so images[] is available for the logo.
  useEffect(() => {
    if (id || !isStudioRole || !admin?.studio?._id) return;
    apiService.getStudioById(admin.studio._id).then((res: any) => {
      applyBranding(res.studio || res);
    }).catch(() => applyBranding(admin.studio));
  }, [id, isStudioRole, admin?.studio, applyBranding]);

  // The chosen location issues the invoice, so it also supplies the branding.
  useEffect(() => {
    if (!mustPickBranch || !invoiceBranch) return;
    apiService.getStudioById(invoiceBranch).then((res: any) => {
      applyBranding(res.studio || res);
    }).catch(() => {});
  }, [mustPickBranch, invoiceBranch, applyBranding]);

  useEffect(() => {
    if (isSuperAdmin) {
      apiService.getStudios({ limit: 500 }).then((res: any) => {
        setStudios(res.data || res.studios || []);
      }).catch(() => {});
    }
  }, [isSuperAdmin]);

  const refreshNextNumber = useCallback(async (studioIdForNumber?: string) => {
    try {
      const res: any = await apiService.getInvoiceNextNumber(studioIdForNumber);
      if (res?.invoiceNumber) setInvoiceNumber(res.invoiceNumber);
    } catch { /* keep current */ }
  }, []);

  useEffect(() => {
    if (id) return;
    if (isStudioRole) { refreshNextNumber(undefined); return; }
    if (brandSource === 'sweatsocial') {
      applyBranding(null);
      refreshNextNumber(undefined);
    } else if (selectedStudioId) {
      apiService.getStudioById(selectedStudioId).then((res: any) => {
        applyBranding(res.studio || res);
      }).catch(() => {});
      refreshNextNumber(selectedStudioId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandSource, selectedStudioId, isStudioRole]);

  const totals = useMemo(
    () => computeInvoiceTotals(items, discount, shipping),
    [items, discount, shipping],
  );

  const previewInvoice = useMemo(() => ({
    invoiceNumber,
    invoiceDate,
    dueDate,
    currencySymbol,
    themeColor,
    logoBgColor,
    billFrom,
    billTo,
    lineItems: items,
    discount,
    shipping,
    notes,
    terms,
  }), [invoiceNumber, invoiceDate, dueDate, currencySymbol, themeColor, logoBgColor, billFrom, billTo, items, discount, shipping, notes, terms]);

  const updateItem = (i: number, field: keyof InvoiceLineItem, value: any) => {
    setItems(prev => prev.map((it, idx) => idx === i ? { ...it, [field]: value } : it));
  };
  const addItem = () => setItems(prev => [...prev, emptyItem()]);
  const removeItem = (i: number) => setItems(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);

  const handleQuickAdd = () => {
    const amt = Number(quick.amount);
    if (!quick.name.trim() || isNaN(amt)) { setError('Quick add needs a name and a numeric amount'); return; }
    setError(null);
    setItems(prev => {
      const cleaned = prev.filter(it => it.description.trim() || it.rate);
      cleaned.push({
        description: quick.name.trim(), category: quick.category.trim(),
        quantity: 1, rate: amt, cgstPercent: 2.5, sgstPercent: 2.5,
      });
      return cleaned.length ? cleaned : [emptyItem()];
    });
    setQuick({ name: '', amount: '', category: '' });
  };

  const buildPayload = () => ({
    studioId: isStudioRole ? undefined : (brandSource === 'studio' ? selectedStudioId : null),
    invoiceDate, dueDate: dueDate || undefined, currencySymbol, status,
    themeColor, logoBgColor,
    billFrom, billTo,
    lineItems: items.map(it => ({
      ...it,
      quantity: Number(it.quantity) || 0,
      rate: Number(it.rate) || 0,
      cgstPercent: Number(it.cgstPercent) || 0,
      sgstPercent: Number(it.sgstPercent) || 0,
      taxPercent: (Number(it.cgstPercent) || 0) + (Number(it.sgstPercent) || 0),
    })),
    discount: Number(discount) || 0, shipping: Number(shipping) || 0, notes, terms,
  });

  const handleSave = async () => {
    if (mustPickBranch && !invoiceBranch) {
      setError('Choose which location is issuing this invoice');
      return;
    }
    setSaving(true); setError(null);
    try {
      if (editId) {
        const res: any = await apiService.updateInvoice(editId, buildPayload(), isStudioRole ? invoiceStudioId : undefined);
        setSavedNumber(res?.invoice?.invoiceNumber || invoiceNumber);
      } else {
        const res: any = await apiService.createInvoice(buildPayload(), mustPickBranch ? invoiceBranch : undefined);
        const num = res?.invoice?.invoiceNumber;
        if (num) setInvoiceNumber(num);
        setSavedNumber(num || invoiceNumber);
        refreshNextNumber(isStudioRole ? undefined : (brandSource === 'studio' ? selectedStudioId : undefined));
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    setError(null);
    try {
      await generateInvoicePdf('invoice-pdf-preview', `${invoiceNumber || 'invoice'}.pdf`);
    } catch {
      setError('Could not generate the PDF.');
    } finally {
      setDownloading(false);
    }
  };

  const fieldSx = { mb: 1.5 };
  const rowAmount = (it: InvoiceLineItem) => {
    const base = (Number(it.quantity) || 0) * (Number(it.rate) || 0);
    const tax = base * ((Number(it.cgstPercent) || 0) + (Number(it.sgstPercent) || 0)) / 100;
    return base + tax;
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <ReceiptLong sx={{ color: colors.primary }} />
          <Typography variant="h5" fontWeight={700}>{editId ? 'Edit Invoice' : 'Invoice Generator'}</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button startIcon={<History />} variant="text" onClick={() => navigate('/invoices')}>History</Button>
          <Button startIcon={<Save />} variant="outlined" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : (editId ? 'Update' : 'Save')}
          </Button>
          <Button
            startIcon={downloading ? <CircularProgress size={16} /> : <Download />}
            variant="contained" onClick={handleDownloadPdf} disabled={downloading}
            sx={{ backgroundColor: colors.primary }}
          >
            Download PDF
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {savedNumber && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSavedNumber(null)}>Invoice {savedNumber} saved.</Alert>}

      <Card sx={{ mb: 2, border: `1px solid ${colors.divider}`, backgroundColor: `${colors.primary}06` }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <Box display="flex" alignItems="center" gap={0.5}><FlashOn sx={{ color: colors.warning, fontSize: 20 }} /><Typography variant="body2" fontWeight={700}>Quick add</Typography></Box>
            <TextField size="small" label="Name" value={quick.name} onChange={e => setQuick({ ...quick, name: e.target.value })} sx={{ minWidth: 200, flex: 1 }} />
            <TextField size="small" label="Amount" type="number" value={quick.amount} onChange={e => setQuick({ ...quick, amount: e.target.value })} sx={{ width: 130 }} />
            <TextField size="small" label="Category" value={quick.category} onChange={e => setQuick({ ...quick, category: e.target.value })} sx={{ width: 170 }} />
            <Button variant="contained" startIcon={<Add />} onClick={handleQuickAdd} sx={{ backgroundColor: colors.primary }}>Add</Button>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3, alignItems: 'start' }}>
        <Box>
          <Card sx={{ mb: 2, border: `1px solid ${colors.divider}` }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} mb={1.5} color="primary">Branding (Bill From)</Typography>
              {mustPickBranch && (
                <Box mb={2}>
                  <BranchSelect value={invoiceBranch} onChange={setInvoiceBranch} minRole="owner" />
                </Box>
              )}
              {isSuperAdmin && (
                <Box mb={2}>
                  <ToggleButtonGroup
                    size="small" exclusive value={brandSource}
                    onChange={(_, v) => v && setBrandSource(v)} sx={{ mb: 1.5 }}
                  >
                    <ToggleButton value="sweatsocial">SweatSocial</ToggleButton>
                    <ToggleButton value="studio">A Studio</ToggleButton>
                  </ToggleButtonGroup>
                  {brandSource === 'studio' && (
                    <TextField
                      select fullWidth size="small" label="Studio" value={selectedStudioId}
                      onChange={e => setSelectedStudioId(e.target.value)} sx={fieldSx}
                    >
                      {studios.map(s => <MenuItem key={s._id} value={s._id}>{s.studioName}</MenuItem>)}
                    </TextField>
                  )}
                </Box>
              )}
              <TextField fullWidth size="small" label="Name" value={billFrom.name} onChange={e => setBillFrom({ ...billFrom, name: e.target.value })} sx={fieldSx} />
              <TextField fullWidth size="small" label="Company ID" value={billFrom.companyId || ''} onChange={e => setBillFrom({ ...billFrom, companyId: e.target.value })} sx={fieldSx} />
              <TextField fullWidth size="small" label="Address" multiline minRows={2} value={billFrom.address} onChange={e => setBillFrom({ ...billFrom, address: e.target.value })} sx={fieldSx} />
              <Box display="flex" gap={1.5}>
                <TextField fullWidth size="small" label="Country" value={billFrom.country || ''} onChange={e => setBillFrom({ ...billFrom, country: e.target.value })} sx={fieldSx} />
                <TextField fullWidth size="small" label="Place of Supply" value={billFrom.placeOfSupply || ''} onChange={e => setBillFrom({ ...billFrom, placeOfSupply: e.target.value })} sx={fieldSx} placeholder="Karnataka (29)" />
              </Box>
              <Box display="flex" gap={1.5}>
                <TextField fullWidth size="small" label="GSTIN" value={billFrom.gstin} onChange={e => setBillFrom({ ...billFrom, gstin: e.target.value })} sx={fieldSx} />
                <TextField fullWidth size="small" label="Logo URL" value={billFrom.logoUrl} onChange={e => setBillFrom({ ...billFrom, logoUrl: e.target.value })} sx={fieldSx} />
              </Box>
              <Box display="flex" gap={1.5}>
                <TextField fullWidth size="small" label="Email" value={billFrom.email} onChange={e => setBillFrom({ ...billFrom, email: e.target.value })} sx={fieldSx} />
                <TextField fullWidth size="small" label="Phone" value={billFrom.phone} onChange={e => setBillFrom({ ...billFrom, phone: e.target.value })} sx={fieldSx} />
              </Box>
              <Box display="flex" gap={2} alignItems="center" mt={0.5}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Theme colour</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <input type="color" value={themeColor} onChange={e => setThemeColor(e.target.value)} style={{ width: 40, height: 32, border: 'none', background: 'none', cursor: 'pointer' }} />
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{themeColor}</Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Logo background</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <input type="color" value={logoBgColor} onChange={e => setLogoBgColor(e.target.value)} style={{ width: 40, height: 32, border: 'none', background: 'none', cursor: 'pointer' }} />
                    <Button size="small" variant="text" onClick={() => setLogoBgColor('transparent')} sx={{ minWidth: 0, fontSize: 11 }}>None</Button>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2, border: `1px solid ${colors.divider}` }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} mb={1.5} color="primary">Bill To (Customer)</Typography>
              <TextField fullWidth size="small" label="Customer name" value={billTo.name} onChange={e => setBillTo({ ...billTo, name: e.target.value })} sx={fieldSx} />
              <TextField fullWidth size="small" label="Address" multiline minRows={2} value={billTo.address} onChange={e => setBillTo({ ...billTo, address: e.target.value })} sx={fieldSx} />
              <Box display="flex" gap={1.5}>
                <TextField fullWidth size="small" label="GSTIN / TRN" value={billTo.gstin} onChange={e => setBillTo({ ...billTo, gstin: e.target.value })} sx={fieldSx} />
                <TextField fullWidth size="small" label="Place of supply" value={billTo.placeOfSupply} onChange={e => setBillTo({ ...billTo, placeOfSupply: e.target.value })} sx={fieldSx} />
              </Box>
              <Box display="flex" gap={1.5}>
                <TextField fullWidth size="small" label="Email" value={billTo.email} onChange={e => setBillTo({ ...billTo, email: e.target.value })} sx={fieldSx} />
                <TextField fullWidth size="small" label="Phone" value={billTo.phone} onChange={e => setBillTo({ ...billTo, phone: e.target.value })} sx={fieldSx} />
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2, border: `1px solid ${colors.divider}` }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} mb={1.5} color="primary">Invoice Details</Typography>
              <Box display="flex" gap={1.5} flexWrap="wrap">
                <TextField size="small" label="Invoice #" value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} sx={{ ...fieldSx, flex: 1 }} />
                <TextField select size="small" label="Currency" value={currencySymbol} onChange={e => setCurrencySymbol(e.target.value)} sx={{ ...fieldSx, width: 120 }}>
                  <MenuItem value="₹">₹ INR</MenuItem>
                  <MenuItem value="$">$ USD</MenuItem>
                  <MenuItem value="€">€ EUR</MenuItem>
                  <MenuItem value="£">£ GBP</MenuItem>
                  <MenuItem value="AED ">AED</MenuItem>
                </TextField>
              </Box>
              <Box display="flex" gap={1.5}>
                <TextField fullWidth size="small" type="date" label="Invoice date" InputLabelProps={{ shrink: true }} value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} sx={fieldSx} />
                <TextField fullWidth size="small" type="date" label="Due date" InputLabelProps={{ shrink: true }} value={dueDate} onChange={e => setDueDate(e.target.value)} sx={fieldSx} />
              </Box>
              <TextField select fullWidth size="small" label="Payment status" value={status} onChange={e => {
                  const v = e.target.value as 'draft' | 'sent' | 'paid';
                  setStatus(v);
                  setNotes(prev => (prev === PAID_NOTE || prev === DUE_NOTE || prev.trim() === '') ? noteForStatus(v) : prev);
                }}
                helperText={status === 'paid'
                  ? 'Already paid → counts toward Invoiced income (₹ INR) from the day marked paid.'
                  : 'Payment due → not counted in revenue until you mark it Paid (revenue dates from that day).'}>
                <MenuItem value="paid">Paid (already received)</MenuItem>
                <MenuItem value="sent">Sent — payment due</MenuItem>
                <MenuItem value="draft">Draft — payment due</MenuItem>
              </TextField>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2, border: `1px solid ${colors.divider}` }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="subtitle1" fontWeight={700} color="primary">Items</Typography>
                <Button size="small" startIcon={<Add />} onClick={addItem}>Add line item</Button>
              </Box>
              {items.map((it, i) => (
                <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1, mb: 1.5, p: 1.5, borderRadius: 1, border: `1px solid ${colors.divider}` }}>
                  <Box display="flex" gap={1}>
                    <TextField size="small" fullWidth label="Description" value={it.description} onChange={e => updateItem(i, 'description', e.target.value)} />
                    <IconButton size="small" onClick={() => removeItem(i)} disabled={items.length === 1}><Delete fontSize="small" /></IconButton>
                  </Box>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <TextField size="small" label="Category" value={it.category} onChange={e => updateItem(i, 'category', e.target.value)} sx={{ flex: 1, minWidth: 120 }} />
                    <TextField size="small" type="number" label="Qty" value={it.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)} sx={{ width: 70 }} />
                    <TextField size="small" type="number" label="Rate" value={it.rate} onChange={e => updateItem(i, 'rate', e.target.value)} sx={{ width: 100 }} />
                    <TextField size="small" type="number" label="CGST %" value={it.cgstPercent} onChange={e => updateItem(i, 'cgstPercent', e.target.value)} sx={{ width: 80 }} />
                    <TextField size="small" type="number" label="SGST %" value={it.sgstPercent} onChange={e => updateItem(i, 'sgstPercent', e.target.value)} sx={{ width: 80 }} />
                    <Box sx={{ minWidth: 90, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontWeight: 700 }}>{money(rowAmount(it), currencySymbol)}</Box>
                  </Box>
                </Box>
              ))}
              <Divider sx={{ my: 1.5 }} />
              <Box display="flex" gap={1.5}>
                <TextField size="small" type="number" label="Discount" value={discount} onChange={e => setDiscount(Number(e.target.value))} sx={{ flex: 1 }} />
                <TextField size="small" type="number" label="Shipping" value={shipping} onChange={e => setShipping(Number(e.target.value))} sx={{ flex: 1 }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Subtotal {money(totals.subtotal, currencySymbol)} · CGST {money(totals.cgstTotal, currencySymbol)} · SGST {money(totals.sgstTotal, currencySymbol)} · Total {money(totals.total, currencySymbol)}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2, border: `1px solid ${colors.divider}` }}>
            <CardContent>
              <TextField fullWidth size="small" label="Notes" multiline minRows={2} value={notes} onChange={e => setNotes(e.target.value)} sx={fieldSx} />
              <TextField
                fullWidth size="small" label="Terms & Conditions" multiline minRows={4}
                value={terms}
                onChange={e => setTerms(e.target.value)}
                helperText="One condition per line — shown as bullet points on the invoice"
                sx={fieldSx}
              />
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ position: { lg: 'sticky' }, top: 16 }}>
          <Box sx={{ border: `1px solid ${colors.divider}`, boxShadow: '0 4px 24px rgba(0,0,0,0.06)', overflow: 'auto' }}>
            <InvoicePreview ref={previewRef} captureId="invoice-pdf-preview" invoice={previewInvoice} />
          </Box>
          <Tooltip title="The PDF is an exact capture of this preview">
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
              Live preview — this is exactly what downloads as PDF
            </Typography>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default InvoiceGeneratorPage;
